import { CheckCircle2 } from "lucide-react";
import { FC, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "~/components/ui/alert-dialog";
import { Button } from "~/components/ui/button";

/**
 * Help-center article explaining how case size affects the quality of
 * AI-generated reports. Kept in sync with the "large_case_completed" email
 * template in sky-tech-ai/infra (terraform/ses.tf).
 */
const LARGE_CASE_DOCS_URL =
  "https://docs.sky-tech.ai/article/63-how-case-size-affects-the-quality-of-ai-generated-reports";

interface LargeDocumentAlertProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  launchSky: () => Promise<void> | void;
}

/**
 * Informational dialog shown on the first "Launch Sky" action for a large case
 * (one whose page count exceeds LARGE_CASE_PAGE_THRESHOLD). Large cases are not
 * blocked from processing — this is a friendly heads-up that, on high-volume
 * cases, generating section-specific reports yields more accurate summaries
 * than a single report for the whole file.
 */
export const LargeDocumentAlert: FC<LargeDocumentAlertProps> = ({
  open,
  onOpenChange,
  launchSky,
}) => {
  const [isLaunching, setIsLaunching] = useState(false);

  const handleConfirm = async () => {
    setIsLaunching(true);
    try {
      await launchSky();
    } finally {
      setIsLaunching(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />
            <FormattedMessage
              id="documentsList.largeCaseWarning.title"
              defaultMessage="Your Case Is Ready"
            />
          </AlertDialogTitle>
          <AlertDialogDescription className="text-sm leading-relaxed text-muted-foreground">
            <FormattedMessage
              id="documentsList.largeCaseWarning.body"
              defaultMessage="Your case exceeds 2,000 pages. For optimal accuracy, generate section reports or process selected documents separately. <link>Learn more</link>"
              values={{
                link: (chunks) => (
                  <a
                    href={LARGE_CASE_DOCS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="whitespace-nowrap font-medium text-primary underline underline-offset-2"
                  >
                    {chunks}
                  </a>
                ),
              }}
            />
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => onOpenChange(false)}
            disabled={isLaunching}
          >
            <FormattedMessage id="cancel" defaultMessage="Cancel" />
          </AlertDialogCancel>
          <Button
            onClick={handleConfirm}
            disabled={isLaunching}
            aria-busy={isLaunching}
          >
            <FormattedMessage
              id="documentsList.launchSky"
              defaultMessage="Launch Sky"
            />
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
